import {useQuery} from '@tanstack/react-query';
import {globalApi, lkApi} from 'api';
import {
    CartCompanyResponse,
    CartOrderSubmitParams,
    LocationObject,
    PickupLocationResponse,
} from 'api/dataTypes';
import {useCurrentUser} from 'hooks/useCurrentUser';
import {useMediaQuery} from 'hooks/useMediaQuery';
import Cookies from 'js-cookie';
import {Cart} from 'lib/Cart';
import {breakpoints} from 'lib/breakpoints';
import {useCartAuthContext} from 'modules/Cart/contexts/cartAuthContext';
import {useCartContext} from 'modules/Cart/contexts/cartContext';
import {
    CART_ORDER_FORM_ID,
    ORDER_PAYMENT_OPTIONS,
    PaymentOptionType,
} from 'modules/Cart/lib/constants';
import {useFormErrors} from 'modules/PersonalAccount/hooks/useFormErrors';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useNavigate} from 'react-router';

export enum CartDeliveryType {
    Delivery = 'delivery',
    Pickup = 'pickup',
}

type CartFormValues = {
    name: string;
    surname: string;
    /** id проставляется когда юзер авторизован и выбирает компанию из селекта */
    companyId?: CartCompanyResponse['id'];
    /** имя проставляется когда юзер не авторизован и пишет название компанииии вручную */
    companyName?: string;
    phone: string;
    email: string;
    address?: string;
    comment?: string;
    agreement: boolean;
};

export type PaymentOptionData = {
    id: PaymentOptionType;
    title: string;
};

export function useCartForm() {
    const {
        setOrderFormIsValid,
        setOrderFormIsSubmitting,
        products: productsEntities,
        productsIds,
        setSubmitCountForm,
    } = useCartContext();
    const {withAuth} = useCartAuthContext();
    const {currentUser} = useCurrentUser({disabled: !withAuth});
    const navigate = useNavigate();
    const isAuth = Boolean(currentUser);
    const region = Cookies.get('region') || '';

    const [pickupPoint, setPickupPoint] = useState<PickupLocationResponse>();

    const {data: pickupPoints} = useQuery({
        queryKey: ['pickupPoints', region],
        queryFn: () => lkApi.getPickupLocations({region}),
        staleTime: Infinity,
        retry: false,
        enabled: Boolean(region),
    });

    useEffect(() => {
        const products = productsIds.map((id) => productsEntities[id]);
        const Ecommerce = window.EcommerceKlen;
        if (Ecommerce && window.App) {
            new Ecommerce('cartNavigation', ['startOrder', {goods: products}]);
        }
    }, [productsEntities, productsIds]);

    useEffect(() => {
        const firstPickupPoint = pickupPoints?.[0];
        if (firstPickupPoint) {
            setPickupPoint(firstPickupPoint);
        }
    }, [pickupPoints]);

    const {data: savedLocations} = useQuery({
        queryKey: ['locations'],
        queryFn: () => lkApi.getLocations(),
        staleTime: Infinity,
        retry: false,
        enabled: isAuth,
    });

    const {data: cartCompanies} = useQuery({
        queryKey: ['cartCompanies'],
        queryFn: () => lkApi.getCartCompanies(),
        staleTime: Infinity,
        retry: false,
        enabled: isAuth,
    });

    const {
        control,
        handleSubmit: handleSubmitHookForm,
        formState,
        setValue: setValueForm,
        watch,
        reset: resetFormValues,
        trigger,
    } = useForm<CartFormValues>({
        defaultValues: {
            name: '',
            surname: '',
            phone: '',
            email: '',
            address: '',
            comment: '',
            agreement: true,
            companyId: undefined,
        },
        mode: 'onChange',
    });

    const {isValid, errors, submitCount} = formState;
    const phoneIsTouched = formState.touchedFields['phone'];

    const [focusableFieldName, setFocusableFieldName] =
        useState<keyof CartFormValues>();
    const makeHandleFocus = useCallback(
        (name: keyof CartFormValues) => () => {
            setFocusableFieldName(name);
        },
        [],
    );

    const {shouldShowErrorTooltipFields} = useFormErrors({
        errors,
        fields: ['name', 'surname', 'phone', 'email', 'companyName'],
        focusableFieldName,
    });

    useEffect(() => {
        if (phoneIsTouched) {
            trigger('phone');
        }
    }, [phoneIsTouched, trigger]);

    const [selectedDeliveryLocation, setDeliveryLocation] =
        useState<LocationObject | null>();
    const [payment, setPayment] = useState<PaymentOptionData>(
        ORDER_PAYMENT_OPTIONS[0],
    );

    useEffect(() => {
        if (currentUser) {
            resetFormValues({
                email: currentUser.member.email,
                name: currentUser.member.name,
                surname: currentUser.member.last_name,
                phone: currentUser.member.phone,
            });
        }
    }, [currentUser, resetFormValues]);

    useEffect(() => {
        setOrderFormIsValid(isValid);
    }, [isValid, setOrderFormIsValid]);

    useEffect(() => {
        setSubmitCountForm(submitCount);
    }, [submitCount, setSubmitCountForm]);

    const isLowerThanDesktop = useMediaQuery(breakpoints.down('lg'));
    useEffect(() => {
        if (isLowerThanDesktop && submitCount && !isValid) {
            const cartForm = document.getElementById(CART_ORDER_FORM_ID);
            cartForm?.scrollIntoView({behavior: 'smooth'});
        }
    }, [submitCount, isValid, isLowerThanDesktop]);

    const [deliveryType, setDeliveryType] = useState(CartDeliveryType.Delivery);

    const handleChangeDeliveryTab = useCallback((tabId: string) => {
        setDeliveryType(tabId as CartDeliveryType);
    }, []);

    const [orderId, setOrderId] = useState('');
    // Этот запрос происходит сразу после успешного запроса на создание заказа.
    // Он нужен, чтобы не отправлять сразу на страницу успешного оформления,
    // так как если отправить сразу, то юзер увидит пустой экран пока выполняется
    // запрос за инфой. Поэтому тут сразу выполняем запрос и кладем его в кэш,
    // а поcле этого сразу редиректим на страницу успешного оформления
    const {data: orderSuccessInfo, isSuccess} = useQuery({
        queryKey: ['cartOrderSuccessInfo', orderId],
        queryFn: () => {
            if (orderId) {
                return lkApi.getCartOrderSuccessInfo({orderId});
            }
        },
        staleTime: Infinity,
        retry: false,
        enabled: Boolean(orderId),
    });

    useEffect(() => {
        if (orderSuccessInfo && isSuccess) {
            setOrderFormIsSubmitting(false);
            navigate(`/submitted/${orderId}`);
        }
    }, [
        orderSuccessInfo,
        isSuccess,
        navigate,
        orderId,
        setOrderFormIsSubmitting,
    ]);

    const handleSubmit = useCallback(
        async (formData: CartFormValues) => {
            setOrderFormIsSubmitting(true);

            const submitData = prepareSubmitData({
                formData,
                pickupPoint,
                payment,
                deliveryType,
                location: selectedDeliveryLocation,
            });

            try {
                const orderResponse =
                    await globalApi.cartOrderSubmit(submitData);

                if (!orderResponse.error) {
                    Cart.reset();
                    //ответ в формате
                    // {
                    //     "redirect": "/shop/cart/info/AB2E4C9C-0A84-0B8A-5913-FEB7BFDFF3C8/"
                    // }
                    const orderId = orderResponse.data.redirect.split('/')[4];
                    if (orderId) {
                        setOrderId(orderId);
                    } else {
                        // eslint-disable-next-line no-console
                        console.error('Not found orderId');
                        setOrderFormIsSubmitting(false);
                    }
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
                setOrderFormIsSubmitting(false);
            }
        },
        [
            pickupPoint,
            payment,
            setOrderFormIsSubmitting,
            deliveryType,
            selectedDeliveryLocation,
        ],
    );

    const handleSelectPickupPoint = useCallback(
        (point: PickupLocationResponse) => {
            setPickupPoint(point);
        },
        [],
    );

    const handleSelectPayment = useCallback((payment: PaymentOptionData) => {
        setPayment(payment);
    }, []);

    const makeHandleSelectLocation = useCallback(
        (location: LocationObject | 'new') => () => {
            if (location === 'new') {
                setDeliveryLocation(null);
            } else {
                setDeliveryLocation(location);
            }
        },
        [],
    );

    const handleSelectCompany = useCallback(
        (company: CartCompanyResponse) => {
            setValueForm('companyId', company.id);
        },
        [setValueForm],
    );

    const companies = useMemo(() => {
        if (!cartCompanies) {
            return [];
        }

        return cartCompanies.map((company) => ({
            ...company,
            label: company.title,
        }));
    }, [cartCompanies]);

    const selectedCompanyId = watch('companyId');

    return {
        control,
        deliveryType,
        pickupPoint,
        payment,
        handleSelectPayment,
        handleChangeDeliveryTab,
        handleSubmit: handleSubmitHookForm(handleSubmit),
        handleSelectPickupPoint,
        makeHandleSelectLocation,
        selectedDeliveryLocation,
        isAuth,
        phoneIsTouched,
        companies,
        handleSelectCompany,
        selectedCompanyId,
        pickupPoints: pickupPoints,
        savedLocations: savedLocations || [],
        makeHandleFocus,
        shouldShowErrorTooltipFields,
        submitCount,
    };
}

function prepareSubmitData({
    formData,
    pickupPoint,
    payment,
    deliveryType,
    location,
}: {
    formData: CartFormValues;
    pickupPoint?: PickupLocationResponse;
    payment: PaymentOptionData;
    deliveryType: CartDeliveryType;
    location?: LocationObject | null;
}): CartOrderSubmitParams {
    // const formData = new FormData();
    // formData.append('name', data.name);
    // formData.append('phone', data.phone);
    // formData.append('email', data.email);
    // formData.append('shipping', deliveryType);
    // pickupPoint && formData.append('pickup', String(pickupPoint.id));
    // formData.append('payment', payment.id);
    // data.comment && formData.append('comment', data.comment);
    // data.agreement && formData.append('subscribe', '1');

    // return formData;

    const submitData: CartOrderSubmitParams = {
        name: formData.name + ' ' + formData.surname,
        phone: formData.phone,
        email: formData.email,
        shipping: deliveryType,
        payment: payment.id,
        comment: formData.comment,
        subscribe: formData.agreement ? '1' : undefined,
        companyName: formData.companyName,
        companyId: formData.companyId,
    };

    if (deliveryType === CartDeliveryType.Pickup && pickupPoint) {
        submitData.pickup = String(pickupPoint.id);
    }

    if (deliveryType === CartDeliveryType.Delivery) {
        submitData.address = location ? location.address : formData.address;
    }

    return submitData;
}
